// release-ci-gate-approved-candidate.ts
//
// CI check for a G3 that is ALREADY approved (dogfood 2026-10-01): an older CLI
// — or a person looking at a still-running pipeline — approved G3, so the
// pre-G3 gate in supervisor-release-flow.ts never ran. Before the first deploy
// side effect the approved leg asks this module: did the MR's CI pass FOR THE
// COMMIT THIS G3 APPROVED? That commit is what the draft-MR push put on the
// branch (`pushed_sha`, journaled on the `git_push` intent — recorded-draft-mr-push.ts;
// the candidate itself is the mission HEAD, which is never pushed as-is).
//
// Fails CLOSED: a journal without that push, a host that cannot be reached (or a
// lookup that throws), an MR/pipeline that belongs to another commit, a status
// that is not a pass — all stop the deploy with a reason. The target branch
// moving since the push is NOT a CI question here (G3 binds candidate +
// manifest, not the base), so the base SHA is not re-checked: the evidence's own
// base is passed to X2's binding — an empty one included (F-B12-1 waits only pre-G3).
import { clipUntrustedText } from "./clip-untrusted-text.js";
import { collectRemoteEvidenceWithoutThrowing } from "./collect-remote-evidence-without-throwing.js";
import type { MissionState } from "./mission-state-types.js";
import { findRecordedPush, type PushDestination } from "./recorded-draft-mr-push.js";
import { releaseCiGateOutcome } from "./release-ci-gate.js";
import { checkRemoteEvidenceBoundToPushedCandidate } from "./remote-evidence-candidate-binding.js";
import type {
  CollectRemoteEvidenceDeps,
  CollectRemoteEvidenceInput,
} from "./review-remote-adapter.js";
import type { ReleaseCiNotPassedOutcome } from "./supervisor-release-flow-resume-outcome.js";

type JournalView = Pick<MissionState, "intents" | "results">;

/** The commit the newest successful draft-MR push of this candidate put on the branch. */
export function findPushedShaForCandidate(
  state: JournalView,
  candidateSha: string,
): string | undefined {
  return findRecordedPush(state, candidateSha)?.pushedSha;
}

export interface VerifyCiOfApprovedCandidateInput {
  state: JournalView;
  candidateSha: string;
  baseSha: string;
  /** Round 3 L-2: only a push to THIS release's destination counts as the push being deployed. */
  destination?: PushDestination;
  remoteEvidenceInput: Omit<CollectRemoteEvidenceInput, "expectedBaseSha" | "pushedSha">;
  remoteEvidenceDeps?: CollectRemoteEvidenceDeps;
}

const STOP = "không deploy";
const RERUN_ADVICE = "Chạy lại `avatar autopilot run` khi MR có pipeline đã pass của commit này.";

/** Fail closed: every reason names what it holds back (`STOP`) and how to clear it. */
const notPassed = (reason: string, advice = RERUN_ADVICE): ReleaseCiNotPassedOutcome => ({
  kind: "release_ci_not_passed",
  reason: `${reason} ${advice}`,
});

/** `undefined` = the MR pipeline of the pushed commit passed. */
export async function verifyCiOfApprovedCandidate(
  input: VerifyCiOfApprovedCandidateInput,
): Promise<ReleaseCiNotPassedOutcome | undefined> {
  const pushedSha = findRecordedPush(input.state, input.candidateSha, input.destination)?.pushedSha;
  if (pushedSha === undefined) {
    // Round 2 L2: the push is only made before G3, so a re-run cannot add it —
    // the exits that exist are a new candidate (new G3) or giving the mission up.
    return notPassed(
      `journal của mission không có lần push Draft MR nào thành công cho candidate ${input.candidateSha} ` +
        `(vd G3 do bản CLI cũ xin) — không biết CI phải chạy trên commit nào ⇒ ${STOP}.`,
      "`run` lại không sửa được điều này. Lối thoát: `avatar autopilot redirect --text " +
        '"<mô tả>"` để dựng candidate mới (xin G3 mới), hoặc `avatar autopilot archive` để bỏ mission.',
    );
  }
  const remote = await collectRemoteEvidenceWithoutThrowing(
    { ...input.remoteEvidenceInput, expectedBaseSha: input.baseSha, pushedSha },
    input.remoteEvidenceDeps,
  );
  if (remote.kind !== "ok") {
    return notPassed(
      `Không lấy được bằng chứng CI của MR để kiểm trước khi deploy: ${clipUntrustedText(remote.reason, 400)} ⇒ ${STOP}.`,
    );
  }
  const { evidence } = remote;
  const binding = checkRemoteEvidenceBoundToPushedCandidate(
    evidence,
    { pushedSha, baseSha: evidence.baseSha },
    STOP,
  );
  if (!binding.ok) return notPassed(binding.reason);
  return releaseCiGateOutcome(evidence, "deploy");
}
